import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logotipo.svg";
import { useAuth } from "@/features/login/hooks/useAuth";

const links = [
  { label: "Nodos", href: "#servicios" },
  { label: "Panel", href: "#panel" },
  { label: "Planes", href: "#planes" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-white/10 bg-nodric-navy/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav
        aria-label="Principal"
        className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 md:px-12"
      >
        <Link to="/" className="flex items-center">
          <span className="sr-only">Nodric</span>
          <img src={logo} alt="Nodric" className="h-6 w-auto md:h-7" />
        </Link>

        <div className="font-display hidden items-center gap-10 lg:flex">
          {links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="relative text-[13px] tracking-[0.16em] text-white/85 uppercase transition-colors hover:text-white after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-nodric-blue after:transition-all after:duration-300 hover:after:w-full"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4">
          {!loading && (
            <Link
              to={user ? "/admin" : "/login"}
              className="font-display hidden text-[13px] tracking-[0.16em] text-white/85 uppercase transition-colors hover:text-nodric-blue lg:block"
            >
              {user ? "Panel" : "Iniciar sesión"}
            </Link>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
            className="cursor-pointer rounded-full border border-white/20 p-2 text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-nodric-navy/95 backdrop-blur-xl lg:hidden"
          >
            <div className="font-display flex flex-col gap-5 px-6 py-6">
              {links.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.16em] text-white/85 uppercase"
                >
                  {link.label}
                </a>
              ))}
              {!loading && (
                <Link
                  to={user ? "/admin" : "/login"}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.16em] text-nodric-blue uppercase"
                >
                  {user ? "Panel" : "Iniciar sesión"}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
